import type { JSONContent } from "@tiptap/core";

/**
 * SharePoint links found inside a page's content, used to nudge editors to
 * register them as source documents. A content link is navigation only — the
 * chat assistant never reads it — so surfacing the gap is how we stop useful
 * documents from silently staying out of the assistant's reach. Promoting a
 * detected link stays a deliberate act (it inherits the page's audience), so
 * this module only detects and names; it never registers anything.
 */
export interface DetectedLink {
  href: string;
  /** The anchor text the editor wrote, used as the default document name. */
  text: string;
}

/** Matches `sharepoint.com` and any subdomain of it (including `*-my` personal
 * OneDrive hosts), https only — the same surface Graph ingestion can reach. */
export function isSharepointUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol !== "https:") return false;
  const host = url.hostname.toLowerCase();
  return host === "sharepoint.com" || host.endsWith(".sharepoint.com");
}

/**
 * Case-fold the origin (hosts are case-insensitive) but keep the path and
 * query verbatim — SharePoint encodes document identity in case-sensitive
 * tokens, so over-normalising would merge distinct files and hide a genuinely
 * new document. Drops the fragment. Used only to compare two URLs for "same
 * document" when deduping against already-registered links.
 */
export function normalizeUrl(value: string): string {
  try {
    const url = new URL(value);
    url.hash = "";
    return `${url.protocol}//${url.host.toLowerCase()}${url.pathname}${url.search}`;
  } catch {
    return value.trim();
  }
}

/** Walk a TipTap document and collect every distinct SharePoint link, keeping
 * the first anchor text seen for each. */
export function extractSharepointLinks(doc: JSONContent | null | undefined): DetectedLink[] {
  const out: DetectedLink[] = [];
  const seen = new Set<string>();

  const visit = (node: JSONContent | undefined): void => {
    if (!node || typeof node !== "object") return;
    const href = node.marks?.find((mark) => mark.type === "link")?.attrs?.href;
    if (typeof href === "string" && isSharepointUrl(href)) {
      const key = normalizeUrl(href);
      if (!seen.has(key)) {
        seen.add(key);
        out.push({ href, text: typeof node.text === "string" ? node.text : "" });
      }
    }
    node.content?.forEach(visit);
  };

  visit(doc ?? undefined);
  return out;
}

/** Cheap equality so the editor only re-renders when the link set actually
 * changes, not on every keystroke. */
export function sameLinks(a: DetectedLink[], b: DetectedLink[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((link, i) => link.href === b[i]?.href && link.text === b[i]?.text);
}

/** A sensible default name for a detected link: prefer the anchor text, else
 * the SharePoint `file=` parameter, else the last path segment. */
export function suggestedName(link: DetectedLink): string {
  const text = link.text.trim();
  if (text && !/^https?:\/\//i.test(text)) return text.slice(0, 200);
  try {
    const url = new URL(link.href);
    const file = url.searchParams.get("file");
    if (file) return decodeURIComponent(file);
    const segment = url.pathname.split("/").filter(Boolean).pop();
    if (segment) return decodeURIComponent(segment);
    return url.hostname;
  } catch {
    return link.href;
  }
}
